import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { ApiError } from '../lib/api'
import { Alert, Button, Input } from '../components/ui'
import { Logo } from '../components/Logo'
import './auth.css'

export default function ForgotPassword() {
  const { resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await resetPassword(email)
      setSent(true)
    } catch (err) {
      // Never reveal whether an address is registered - that is an account
      // enumeration leak. The backend already returns ok:true regardless of
      // whether the email exists; only a malformed address or a genuine
      // network failure reaches this catch at all.
      if (err instanceof ApiError && err.code === 'invalid_email') {
        setError('That does not look like a valid email address.')
      } else if (err instanceof TypeError) {
        setError('Cannot reach the server. Check your internet connection.')
      } else {
        setSent(true)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <main className="auth__panel" style={{ gridColumn: '1 / -1' }}>
        <div className="auth__form">
          <div style={{ marginBottom: 'var(--sp-8)' }}><Logo /></div>

          {sent ? (
            <>
              <h1 className="auth__title">Check your email</h1>
              <p className="auth__sub">
                If an account exists for <strong>{email}</strong>, we&rsquo;ve sent a link
                to reset your password. The link expires in one hour.
              </p>
              <Alert tone="info">
                Not seeing it? Check your spam folder, and confirm you used the
                address your administrator registered.
              </Alert>
              <p className="auth__foot"><Link to="/login">Back to sign in</Link></p>
            </>
          ) : (
            <>
              <h1 className="auth__title">Reset your password</h1>
              <p className="auth__sub">
                Enter your email and we&rsquo;ll send you a link to set a new password.
              </p>
              <form onSubmit={onSubmit} noValidate>
                <div className="auth__fields">
                  {error && <Alert tone="danger">{error}</Alert>}
                  <Input
                    label="Email address"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" size="lg" block loading={busy}>
                    {busy ? 'Sending…' : 'Send reset link'}
                  </Button>
                </div>
              </form>
              <p className="auth__foot"><Link to="/login">Back to sign in</Link></p>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
